import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button as DSButton } from '../../components/DesignSystem/components/Button';
import { Heading, Text } from '../../components/DesignSystem/components/Typography';
import { Container, Section, Grid } from '../../components/DesignSystem/components/Layout';
import { Card } from '../../components/DesignSystem/components/Card';
import './Home.css';

// Import local assets if available or use placeholders
// Ideally, you should import your local images here.
// For now, I will use the external links provided in code.html as placeholders
// allowing you to replace them with your local assets later.
import logo2 from "../../assets/images/logo2.png";
import logo_heart from "../../assets/images/heart-logo.png";
import youtube from "../../assets/images/youtube.png";
import tiktok from "../../assets/images/tiktok.png";
import gram from "../../assets/images/gram.png";
import r_bg from "../../assets/images/r-bg.png";
import lp11 from "../../assets/images/lp11.png";
import lp22 from "../../assets/images/lp22.png";
import lp33 from "../../assets/images/lp33.png";
import vd1 from "../../assets/images/vd1.png";
import vd2 from "../../assets/images/vd2.png";
import vd3 from "../../assets/images/vd3.png";
import profile1 from "../../assets/images/profile1.png";
import profile2 from "../../assets/images/profile2.png";
import profile3 from "../../assets/images/profile3.png";


import { Navbar } from '../../components/DesignSystem/components/Navbar';
import { Footer } from '../../components/DesignSystem/components/Footer';
import OptimizedImage from '../../components/OptimizedImage/OptimizedImage';

const FadeInSection = ({ children, delay = 0, className = "" }) => {
    const [isVisible, setVisible] = useState(false);
    const domRef = useRef();

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.15, rootMargin: "0px 0px -50px 0px" });
        
        if (domRef.current) observer.observe(domRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={domRef} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
            {children}
        </div>
    );
};

const Home = () => {
    const navigate = useNavigate();
    const [modalShow, setModalShow] = useState(false);
    const [videoURL, setVideoURL] = useState("");
    const videoScrollRef = useRef(null);

    useEffect(() => {
        const scrollContainer = videoScrollRef.current;
        if (!scrollContainer) return;

        const scrollInterval = setInterval(() => {
            // Check if scroll is necessary (content wider than container)
            // This effectively targets mobile where overflow exists
            if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
                const cardWidth = scrollContainer.children[0] ? scrollContainer.children[0].offsetWidth : 0;
                const gap = 16; // 1rem gap estimated
                const scrollAmount = cardWidth + gap;
                const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
                const currentScroll = scrollContainer.scrollLeft;

                let nextScroll = currentScroll + scrollAmount;

                // Loop back to start if we've reached (or passed) the end
                if (currentScroll >= maxScroll - 10) {
                    nextScroll = 0;
                }

                scrollContainer.scrollTo({
                    left: nextScroll,
                    behavior: 'smooth'
                });
            }
        }, 3000);

        return () => clearInterval(scrollInterval);
    }, []);

    const handleSuccessStoryClick = () => {
        window.open("https://tamilculture.com/mytamildate-success-abi-john-bonded-over-faith-their-tamil-german-british-connection", "_blank");
    };

    return (
        <div className="lp-bg-background-light lp-text-action-black font-display">
            {/* Navigation */}
            <Navbar />

            <main style={{ width: '100%' }}>
                <Container>

                {/* Hero Section */}
                <section className="hero-section lp-mb-20">
                    <div className="hero-content lp-order-2-mobile lp-order-1-lg">
                         {/* Centered content for mobile, left for desktop via CSS */}
                        {/*<div className="hero-stats-badge lp-mb-8">
                            <span className="material-symbols-outlined lp-text-primary lp-text-sm">verified</span>
                            <span className="lp-text-primary lp-text-xs lp-font-bold lp-uppercase lp-tracking-widest">107K+ Meaningful Matches</span>
                        </div>*/}
                        <Heading variant="h1" className="hero-title lp-font-black lp-text-action-black lp-mb-4">
                            Find Your <span className="lp-text-primary">Meaningful</span> Connection
                        </Heading>
                        <br/>
                        <Text size="lg" className="hero-subtitle lp-md-text-xl lp-text-gray-600">
                            Voices you love. Discover someone who truly gets you in a safe, friendly space designed for real, human moments.
                        </Text>
                        <br/>
                        <div className="hero-actions">
                             <DSButton to="/signup" variant="primary" size="lg" className="lp-shadow-xl">
                                    Get Started
                             </DSButton>
                            <DSButton to="/aboutus" variant="secondary" size="lg">
                                    Learn More
                            </DSButton>
                        </div>                        

                    </div>

                    <div className="hero-image-wrapper lp-order-1-mobile lp-order-2-lg">
                        <div className="hero-image-container">
                            <OptimizedImage className="hero-image" alt="Hero Image" src={r_bg} priority={true} />
                            
                            <div className="hero-floating-card lp-hidden lp-md-block">
                                <div className="hero-floating-content">
                                    <div>
                                        <h3 className="lp-font-bold lp-text-xl lp-text-action-black">Start Something Real</h3>
                                        <p className="lp-text-sm lp-text-gray-600">Where intentions actually matter</p>
                                    </div>
                                    <div className="hero-floating-icon">
                                        <span className="material-symbols-outlined lp-text-white">favorite</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="lp-mb-20">
    <FadeInSection>
    <div className="features-banner-container">
        <div className="feature-banner-block">
            <div className="feature-banner-icon">
                <span className="material-symbols-outlined">verified_user</span>
            </div>
            <h3 className="feature-banner-title">All profiles verified</h3>
            <p className="feature-banner-text">
                We ensure every profile is authentic. No bots, no fakes—just real people looking for real connections.
            </p>
        </div>

        <div className="feature-banner-block">
            <div className="feature-banner-icon">
                <span className="material-symbols-outlined">psychology</span>
            </div>
            <h3 className="feature-banner-title">Compatibility-based</h3>
            <p className="feature-banner-text">
                Matches based on deep compatibility factors, values, and life goals. Not just a superficial swipe.
            </p>
        </div>

        <div className="feature-banner-block">
            <div className="feature-banner-icon">
                <span className="material-symbols-outlined">security</span>
            </div>
            <h3 className="feature-banner-title">Safe & Secure</h3>
            <p className="feature-banner-text">
                Your data and privacy are our priority. Secure messaging and smart safety tools keep you protected.
            </p>
        </div>
    </div>
    </FadeInSection>
</section>
            {/* Success Story Section */}
                <section className="lp-mb-20 lp-bg-white lp-rounded-xl lp-p-card">
                    <FadeInSection>
                    <Grid columns={1} className="lp-grid-cols-2-lg lp-gap-18 lp-items-center">
                        {/* Image: Order 2 on Mobile, Order 1 on Desktop */}
                        <div className="lp-order-2-mobile lp-order-1-lg">
                             <OptimizedImage alt="Abi and John" className="lp-rounded-lg lp-w-full lp-aspect-[4/3] lp-object-cover lp-shadow-xl" src={lp11} style={{ width: '100%', borderRadius: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', aspectRatio: '4/3', objectFit: 'cover' }} />
                        </div>

                        {/* Text: Order 1 on Mobile, Order 2 on Desktop */}
                        <div className="lp-order-1-mobile lp-order-2-lg lp-flex lp-flex-col lp-gap-6 lp-items-center lp-text-center lp-flex-left-lg lp-text-left-lg">
                            <Heading variant="h2" className="lp-text-4xl lp-md-text-5xl lp-font-black lp-leading-tight lp-text-action-black">
                                Discover Love with <span className="text-[#FF7A4E]">myTamilDate</span>
                            </Heading>
                            <Text size="lg" className="lp-md-text-xl lp-text-gray-600 lp-leading-relaxed">
                                A surprise engagement at a myTamilDate couple's photoshoot! Find out how Abi & John Bonded Over Faith & Their Tamil-German-British Connection.
                            </Text>
                            <DSButton 
                                onClick={handleSuccessStoryClick}
                                variant="primary"
                                size="lg"
                                className="success-btn"
                            >
                                <span className="lp-block lp-md-hidden">Read more</span>
                                <span className="lp-hidden lp-md-block">View Success Story</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </DSButton>
                        </div>
                    </Grid>
                    </FadeInSection>
                </section>

                {/* Community Section: Meet like-minded people */}
                <section className="lp-mb-20 lp-bg-white lp-rounded-xl lp-p-card">
                    <FadeInSection>
                    <Grid columns={1} className="lp-grid-cols-2-lg lp-gap-18 lp-items-center">
                        <div className="lp-order-1-mobile lp-order-1-lg lp-flex lp-flex-col lp-gap-6 lp-items-center lp-text-center lp-flex-left-lg lp-text-left-lg">
                            <Heading variant="h2" className="lp-text-4xl lp-md-text-5xl lp-font-black lp-leading-tight lp-text-action-black">Meet like minded people from your community</Heading>
                            <Text size="lg" className="lp-md-text-xl lp-text-gray-600 lp-leading-relaxed">
                                Connect with others who truly understand your unique quirks, share your passions, and appreciate your inside jokes and who value what makes you, you.
                            </Text>
                            <DSButton 
                                to="/signup" 
                                variant="primary" 
                                size="lg"
                                className="success-btn" 
                            >
                                <span className="lp-block lp-md-hidden">Join now</span>
                                <span className="lp-hidden lp-md-block">Join the community</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </DSButton>
                        </div>
                        <div className="lp-relative lp-order-2-mobile lp-order-2-lg">
                            <OptimizedImage alt="Happy Couple" className="lp-rounded-lg lp-w-full lp-aspect-[4/3] lp-object-cover lp-shadow-xl img-position-right" src={lp22} style={{ width: '100%', borderRadius: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', aspectRatio: '4/3', objectFit: 'cover' }} />
                            <div className="decorative-circle decorative-circle-right lp-hidden lp-md-block"></div>
                        </div>
                    </Grid>
                    </FadeInSection>
                </section>

                {/* Duplicated Community Section: Meaningful Connections */}
                <section className="lp-mb-20 lp-bg-white lp-rounded-xl lp-p-card">
                    <FadeInSection>
                    <Grid columns={1} className="lp-grid-cols-2-lg lp-gap-18 lp-items-center">
                        <div className="lp-relative lp-order-2-mobile lp-order-1-lg">
                            <OptimizedImage alt="Meaningful Connections" className="lp-rounded-lg lp-w-full lp-aspect-[4/3] lp-object-cover lp-shadow-xl" src={lp33} style={{ width: '100%', borderRadius: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', aspectRatio: '4/3', objectFit: 'cover' }} />
                            <div className="decorative-circle decorative-circle-left lp-hidden lp-md-block"></div>
                        </div>
                        <div className="lp-flex lp-flex-col lp-gap-6 lp-order-1-mobile lp-order-2-lg lp-items-center lp-text-center lp-flex-left-lg lp-text-left-lg">
                            <Heading variant="h2" className="lp-text-4xl lp-md-text-5xl lp-font-black lp-leading-tight lp-text-action-black">Discover meaningful connections</Heading>
                            <Text size="lg" className="lp-md-text-xl lp-text-gray-600 lp-leading-relaxed">
                                Meet people who share your interests, values, and that undeniable spark who are also looking for a meaningful match. Whether they're around the corner or across the globe, we'll help you find your match.
                            </Text>
                        </div>
                    </Grid>
                    </FadeInSection>
                </section>

                {/* Social Proof Stats */}
                <section className="lp-mb-20">
                    <FadeInSection>
                    <div className="lp-text-center lp-mb-12">
                        <Heading variant="h2" className="lp-text-4xl lp-md-text-5xl lp-font-black lp-leading-tight lp-mb-4">Dating While Tamil?</Heading>
                        <Text size="lg" className="lp-md-text-xl lp-text-gray-600 lp-leading-relaxed">Check out the MTD community's tips & insights!</Text>
                    </div>
                    <div className="video-grid" ref={videoScrollRef}>
                        <div className="video-card group clickable-card" onClick={() => {
                            setVideoURL("https://www.instagram.com/reel/CbiMcvWDt_V/embed/");
                            setModalShow(true);
                        }}>
                            <OptimizedImage alt="Video story" className="lp-w-full lp-h-full lp-object-cover group-hover:scale-105 lp-transition-transform lp-duration-500" src={vd1} />
                            <div className="video-overlay">
                                <div className="lp-flex lp-items-center lp-gap-2">
                                    <span className="material-symbols-outlined lp-text-white lp-text-4xl">play_circle</span>
                                    <Text weight="bold" color="inverse">GF Rates My Outfit</Text>
                                </div>
                            </div>
                        </div>
                        <div className="video-card group clickable-card" onClick={() => {
                             setVideoURL("https://www.instagram.com/reel/C6EPQwmvOPp/embed/");
                             setModalShow(true);
                        }}>
                            <OptimizedImage alt="Video story" className="lp-w-full lp-h-full lp-object-cover group-hover:scale-105 lp-transition-transform lp-duration-500" src={vd2} />
                            <div className="video-overlay">
                                <div className="lp-flex lp-items-center lp-gap-2">
                                    <span className="material-symbols-outlined lp-text-white lp-text-4xl">play_circle</span>
                                    <Text weight="bold" color="inverse">Cultural Connection</Text>
                                </div>
                            </div>
                        </div>
                        <div className="video-card group clickable-card" onClick={() => {
                            setVideoURL("https://www.instagram.com/reel/CsT1_nlB5VR/embed/");
                            setModalShow(true);
                        }}>
                            <OptimizedImage alt="Video story" className="lp-w-full lp-h-full lp-object-cover group-hover:scale-105 lp-transition-transform lp-duration-500" src={vd3} />
                            <div className="video-overlay">
                                <div className="lp-flex lp-items-center lp-gap-2">
                                    <span className="material-symbols-outlined lp-text-white lp-text-4xl">play_circle</span>
                                    <Text weight="bold" color="inverse">MTD Success Tips</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                    </FadeInSection>
                </section>

                <VerticallyCenteredModal
                    show={modalShow}
                    onHide={() => setModalShow(false)}
                    videoURL={videoURL}
                />
                </Container>
            </main>

            {/* Footer */}
            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Home;

function VerticallyCenteredModal(props) {
    if(!props.show) return null;
    return (
        <div className="lp-modal-backdrop" onClick={props.onHide}>
            <div className="lp-modal-content" onClick={e => e.stopPropagation()}>
                <button className="lp-modal-close" onClick={props.onHide}>&times;</button>
                <div className="lp-modal-body">
                    <iframe
                        className="lp-modal-iframe"
                        title='reel'
                        src={props.videoURL}
                        frameBorder="0"
                        allowFullScreen={true}
                    ></iframe>
                </div>
            </div>
        </div>
    );
}
